import { useState, useRef, useEffect, useMemo } from 'react'
import { MdSearch, MdClose, MdCheckCircle, MdExpandMore } from 'react-icons/md'

export interface SearchableSelectOption {
    value: string
    label: string
    description?: string
    badge?: string
    badgeClass?: string
    group?: string
}

interface SearchableSelectProps {
    options: SearchableSelectOption[]
    value?: string
    onChange: (value: string, option: SearchableSelectOption | null) => void
    placeholder?: string
    searchPlaceholder?: string
    disabled?: boolean
    className?: string
    noResultsText?: string
    maxHeight?: string
}

export default function SearchableSelect({
    options,
    value = '',
    onChange,
    placeholder = 'Selecionar...',
    searchPlaceholder = 'Buscar...',
    disabled = false,
    className = '',
    noResultsText = 'Nenhum resultado encontrado',
    maxHeight = 'max-h-64',
}: SearchableSelectProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState(-1)

    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLUListElement>(null)

    const selectedOption = useMemo(
        () => options.find(o => o.value === value) || null,
        [options, value],
    )

    const filtered = useMemo(() => {
        if (!search.trim()) return options
        const term = search.toLowerCase()
        return options.filter(
            o =>
                o.label.toLowerCase().includes(term) ||
                o.description?.toLowerCase().includes(term) ||
                o.badge?.toLowerCase().includes(term) ||
                o.group?.toLowerCase().includes(term),
        )
    }, [options, search])

    // Agrupar por group se existir
    const grouped = useMemo(() => {
        const hasGroups = filtered.some(o => o.group)
        if (!hasGroups) return null

        const map = new Map<string, SearchableSelectOption[]>()
        for (const opt of filtered) {
            const key = opt.group || ''
            if (!map.has(key)) map.set(key, [])
            map.get(key)!.push(opt)
        }
        return map
    }, [filtered])

    // Fecha ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Focus no input ao abrir
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 0)
        } else {
            setSearch('')
            setHighlightedIndex(-1)
        }
    }, [isOpen])

    // Scroll para item destacado
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const item = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`) as HTMLElement
            if (item) item.scrollIntoView({ block: 'nearest' })
        }
    }, [highlightedIndex])

    const handleSelect = (option: SearchableSelectOption) => {
        onChange(option.value, option)
        setIsOpen(false)
    }

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation()
        onChange('', null)
        setIsOpen(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : 0))
                break
            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filtered.length - 1))
                break
            case 'Enter':
                e.preventDefault()
                if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
                    handleSelect(filtered[highlightedIndex])
                }
                break
            case 'Escape':
                setIsOpen(false)
                break
        }
    }

    const renderOption = (option: SearchableSelectOption, index: number) => (
        <li key={option.value}>
            <button
                type="button"
                data-index={index}
                onClick={() => handleSelect(option)}
                className={`w-full px-3 py-2 text-left hover:bg-base-200 transition-colors flex items-center gap-2 ${
                    highlightedIndex === index ? 'bg-secondary/10' : ''
                } ${option.value === value ? 'bg-secondary/5 font-medium' : ''}`}
            >
                {option.badge && (
                    <span className={`badge badge-xs shrink-0 ${option.badgeClass || 'badge-ghost'}`}>
                        {option.badge}
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    <span className="text-sm block truncate">{option.label}</span>
                    {option.description && (
                        <span className="text-xs text-base-content/50 block truncate">{option.description}</span>
                    )}
                </div>
                {option.value === value && (
                    <MdCheckCircle className="text-secondary shrink-0" size={16} />
                )}
            </button>
        </li>
    )

    // Flat index counter para keyboard navigation com grupos
    let flatIndex = 0

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {/* Trigger */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`input input-bordered w-full flex items-center justify-between text-left ${
                    selectedOption ? '' : 'text-base-content/50'
                } ${disabled ? 'input-disabled' : ''}`}
            >
                <span className="flex-1 truncate">
                    {selectedOption ? (
                        <span className="flex items-center gap-2">
                            {selectedOption.badge && (
                                <span className={`badge badge-xs ${selectedOption.badgeClass || 'badge-ghost'}`}>
                                    {selectedOption.badge}
                                </span>
                            )}
                            {selectedOption.label}
                        </span>
                    ) : (
                        placeholder
                    )}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                    {selectedOption && !disabled && (
                        <span
                            onClick={handleClear}
                            className="hover:text-error cursor-pointer"
                        >
                            <MdClose size={14} />
                        </span>
                    )}
                    <MdExpandMore size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl overflow-hidden">
                    {/* Campo de busca */}
                    <div className="p-2 border-b border-base-200">
                        <div className="relative">
                            <MdSearch size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40 z-10 pointer-events-none" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={e => {
                                    setSearch(e.target.value)
                                    setHighlightedIndex(-1)
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder={searchPlaceholder}
                                className="input input-bordered input-sm w-full pl-8"
                            />
                        </div>
                    </div>

                    {/* Lista de opções */}
                    <ul ref={listRef} className={`${maxHeight} overflow-y-auto`}>
                        {filtered.length === 0 && (
                            <li className="px-3 py-4 text-center text-sm text-base-content/50">
                                {noResultsText}
                            </li>
                        )}

                        {grouped ? (
                            // Renderizar com grupos
                            Array.from(grouped.entries()).map(([groupName, groupOptions]) => (
                                <li key={groupName}>
                                    {groupName && (
                                        <div className="px-3 py-1.5 text-xs font-semibold text-base-content/50 bg-base-200/50 uppercase tracking-wide">
                                            {groupName}
                                        </div>
                                    )}
                                    <ul>
                                        {groupOptions.map(option => {
                                            const idx = flatIndex++
                                            return renderOption(option, filtered.indexOf(option))
                                        })}
                                    </ul>
                                </li>
                            ))
                        ) : (
                            // Renderizar flat
                            filtered.map((option, index) => renderOption(option, index))
                        )}
                    </ul>
                </div>
            )}
        </div>
    )
}
